<template>
  <div>
    <v-breadcrumbs :items="breadcrumbs" class="px-0" />
    <heading>{{ $t("sidebar.asaMeetings") }} {{ year }}</heading>

    <div class="d-flex justify-end mb-4">
      <asaMeetingTransfer :year="year" @transferred="load" />
      <v-btn v-if="canCreateHere" color="secondary" outlined @click="newMeeting">
        {{ $t("asaMeeting.new") }}
        <v-icon right>add</v-icon>
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="meetings"
      :loading="loading"
      item-key="_id"
      class="elevation-0 asa-table"
      :no-data-text="$t('asaMeeting.noMeetings')"
      @click:row="openMeeting"
    >
      <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
      <template v-slot:item.title="{ item }">
        {{ item.title || $t("sidebar.asaMeetings") }}
      </template>
      <template v-slot:item.participants="{ item }">
        {{ (item.participants || []).length }}
      </template>
      <template v-slot:item.topics="{ item }">{{ (item.topics || []).length }}</template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-end align-center">
          <v-progress-circular v-if="pdfLoading === item._id" indeterminate size="20" width="2" class="mr-2" />
          <v-icon v-else medium class="mr-2" @click.stop="openPdf(item)">picture_as_pdf</v-icon>
          <v-icon v-if="canCreateHere" medium class="mr-2" @click.stop="copyMeeting(item)">content_copy</v-icon>
          <v-icon v-if="!isReadOnlyUser" medium class="mr-2" @click.stop="openMeeting(item)">edit</v-icon>
          <v-icon v-if="canDeleteHere" medium class="mr-2" @click.stop="confirmDelete(item)">delete</v-icon>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="deleteDialog.open" max-width="440">
      <v-card>
        <v-card-title>{{ $t("asaMeeting.deleteTitle") }}</v-card-title>
        <v-card-text>{{ $t("asaMeeting.deleteConfirm") }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog.open = false">{{ $t("general.cancel") }}</v-btn>
          <v-btn color="error" text @click="deleteMeeting">{{ $t("asaMeeting.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { openPdfFromBase64 } from "./asaMeetingPdf";
import asaMeetingTransfer from "./asaMeetingTransfer.vue";

export default {
  name: "asaMeetingList",
  components: { asaMeetingTransfer },
  props: ["year"],
  data: () => ({
    meetings: [],
    loading: false,
    pdfLoading: null,
    deleteDialog: { open: false, item: null },
  }),
  computed: {
    breadcrumbs() {
      return [
        { text: this.$t("sidebar.asaMeetings"), to: { name: "asaMeetingYears" }, exact: true },
        { text: String(this.year), disabled: true },
      ];
    },
    headers() {
      return [
        { text: this.$t("asaMeeting.date"), value: "date" },
        { text: this.$t("asaMeeting.title"), value: "title" },
        { text: this.$t("asaMeeting.participants"), value: "participants", sortable: false },
        { text: this.$t("asaMeeting.topics"), value: "topics", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.meetings = await this.$store.dispatch("getAsaMeetings", this.year);
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    newMeeting() {
      this.$router.push({
        name: "asaMeetingForm",
        params: { id: "new", mode: "new" },
        query: { year: this.year },
      });
    },
    openMeeting(item) {
      this.$router.push({
        name: "asaMeetingForm",
        params: { id: item._id, mode: "edit" },
        query: { year: this.year },
      });
    },
    copyMeeting(item) {
      this.$router.push({
        name: "asaMeetingForm",
        params: { id: item._id, mode: "copy" },
        query: { year: this.year },
      });
    },
    async openPdf(item) {
      this.pdfLoading = item._id;
      try {
        const result = await this.$store.dispatch("getAsaMeetingPdf", item._id);
        if (result && result.base64) {
          openPdfFromBase64(result.base64, this.$t("asaMeeting.pdfTitle"));
        }
      } finally {
        this.pdfLoading = null;
      }
    },
    confirmDelete(item) {
      this.deleteDialog = { open: true, item };
    },
    async deleteMeeting() {
      const item = this.deleteDialog.item;
      this.deleteDialog.open = false;
      if (!item) return;
      await this.$store.dispatch("deleteAsaMeeting", item._id);
      this.load();
    },
  },
};
</script>

<style scoped>
.asa-table >>> tbody tr {
  cursor: pointer;
}
</style>
